// @file Composable that provides methods for draggable section headers
import device from '@@/bits/device'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { Section } from '@@/types'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, type Component, type ComputedRef, type Ref } from 'vue'

export const SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID = 'surface-section-manual-drag-preview'

export const useSurfaceSectionHeaderDraggableElement = (params: {
  sectionTitleRef: Ref<HTMLElement | undefined>
  group: Group
  type: string
  dragPreview: Component | undefined
  dragPreviewComponentProps: ComputedRef<object> | undefined
  axis: 'horizontal' | 'vertical'
  isDraggable: Ref<boolean>
  canDrop: Ref<boolean>
}): {
  groupIdBeingDragged: typeof groupIdBeingDragged
  closestEdgeToPointerForSectionHeader: typeof closestEdgeToPointerForSectionHeader
} => {
  const { sectionTitleRef, group, type, dragPreview, dragPreviewComponentProps, axis, isDraggable, canDrop } = params
  const { isASectionDragging } = storeToRefs(useSurfaceSectionsStore())
  const groupIdBeingDragged = ref<string | number | undefined>(undefined)
  const dragPreviewEl = ref<HTMLElement | undefined>(undefined)

  // Make the section title draggable
  useDraggableElement({
    elementRef: sectionTitleRef,
    type,
    data: computed(() => {
      return { section: group as Section }
    }),
    // For iOS, we need to manually create a drag preview element since there is a bug with the native drag preview.
    dragPreview: device.ios ? undefined : dragPreview,
    dragPreviewComponentProps,
    shouldDisableNativeDragPreview: device.ios,
    onDragStart: (e) => {
      isASectionDragging.value = true
      groupIdBeingDragged.value = group.id

      if (!device.ios) return
      // For iOS, we need to manually create a drag preview element since there is a bug with the native drag preview.
      // We need to use nextTick to ensure that `sectionTitleRef` is in the DOM before cloning.
      nextTick(() => {
        if (sectionTitleRef.value == null) return
        const initialX = e.location.initial.input.pageX
        const initialY = e.location.initial.input.pageY
        dragPreviewEl.value = sectionTitleRef.value.cloneNode(true) as HTMLElement
        dragPreviewEl.value.setAttribute('id', SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)
        dragPreviewEl.value.style.position = 'absolute'
        dragPreviewEl.value.style.top = `${initialY}px`
        dragPreviewEl.value.style.left = `${initialX}px`
        dragPreviewEl.value.style.width = window.getComputedStyle(sectionTitleRef.value).width
        dragPreviewEl.value.style.opacity = '1'
        document.body.appendChild(dragPreviewEl.value)
      })
    },
    onDrag: (e) => {
      if (!device.ios) return
      // For iOS, we need to manually update the drag preview element to follow the pointer.
      if (dragPreviewEl.value) {
        dragPreviewEl.value.style.top = `${e.location.current.input.pageY}px`
        dragPreviewEl.value.style.left = `${e.location.current.input.pageX}px`
      }
    },
    onDrop: () => {
      isASectionDragging.value = false
      groupIdBeingDragged.value = undefined

      if (!device.ios) return
      // For iOS, we need to manually remove the drag preview element.
      dragPreviewEl.value = undefined
      document.getElementById(SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)?.remove()
    },
    isDraggable,
  })
  const { closestEdgeToPointer: closestEdgeToPointerForSectionHeader, addDraggableSource } = useDropTargetElement({
    elementRef: sectionTitleRef,
    type,
    data: computed(() => {
      return { section: group as Section }
    }),
  })

  // Make section header droppable
  addDraggableSource({
    type,
    axis,
    onDrop: (e: OnDropPayload<{ section: Section }, { section: Section }>) => {
      isASectionDragging.value = false
      groupIdBeingDragged.value = undefined
      const sourceSection = e.sourceItem.data.section
      const targetSection = e.targetItem.data.section
      const { sortIndex, gapSize } = useSectionSorterHelper().getSortIndexAndGapSize(
        targetSection.id,
        e.relativePositionToTarget,
      )
      if (sortIndex === undefined || gapSize === undefined) return
      useSurfaceSectionsStore().sortSection({
        sectionId: sourceSection.id,
        sortIndex,
        gapSize,
      })

      if (!device.ios) return
      // For iOS, we need to manually remove the drag preview element.
      dragPreviewEl.value = undefined
      document.getElementById(SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)?.remove()
    },
    canDrop: () => {
      return canDrop.value
    },
  })

  return {
    groupIdBeingDragged,
    closestEdgeToPointerForSectionHeader,
  }
}
